import {
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { API, OrgDocument } from '../../../api';
import { useProgramContext } from '../../../hooks/useProgramContext';
import { useAppSelector } from '../../../redux/hooks';
import ROLES from '../../../utils/roles';
import { getUserDetails } from '../../../utils/orgName';
import ConfirmDialog from '../../../components/DialogUi/ConfirmDialog';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArticleIcon from '@mui/icons-material/Article';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'react-toastify';
import AddFileModal from '../../../components/admin3/newProgram/masterclass/ai/AddFileModal';
import AddTextModal from '../../../components/admin3/newProgram/masterclass/ai/AddTextModal';
import AddURLModal from '../../../components/admin3/newProgram/masterclass/ai/AddURLModal';
import ConnectedSources from '../../../components/integrations/ConnectedSources';
import { Bone, fadeInSx } from '../../../components/ui/DashboardSkeletons';
import { MOBILE_SX } from '../../../mobile/mobileApp';

type PendingDoc = { id: string; name: string; type: 'file' | 'text' | 'webpage' };

type KnowledgeDoc = OrgDocument & { source?: string };

// Docs managed outside this page can't be updated or deleted here (the BE
// refuses them too — shared/utils/orgDocPermissions).
const managedElsewhereNote = (doc: KnowledgeDoc): string | null => {
  if (doc.source === 'sharepoint') return 'Synced from SharePoint. Manage it there.';
  if (doc.source === 'program') return "A course's content. It updates when the course is published.";
  return null;
};

const Knowledge = () => {
  const { groupId: selectedGroupId, orgId } = useProgramContext();
  // Knowledge is org-wide: with no program selected (an org admin on /app/knowledge) it works through
  // the org's own group. Every add used to stop at "No workspace selected" (QA ORG-61/62/63).
  const orgGroupId = useAppSelector((state) => state.orgDetails.data?.grpId) || '';
  const lastseenGroupId = selectedGroupId || orgGroupId;

  const [orgDocs, setOrgDocs] = useState<OrgDocument[]>([]);
  const [pendingDocs, setPendingDocs] = useState<PendingDoc[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [openFileModal, setOpenFileModal] = useState(false);
  const [openTextModal, setOpenTextModal] = useState(false);
  const [openUrlModal, setOpenUrlModal] = useState(false);
  const lastUploadedId = useRef<string | null>(null);

  // Update / Delete from a doc row. Platform and org admins can change any doc
  // in the org; everyone else (program admins, learners) only what they added.
  const { userId } = getUserDetails();
  const role = localStorage.getItem('role');
  const isOrgOrPlatformAdmin = role === ROLES.orgAdmin || role === ROLES.platAdmin;
  const canChange = (doc: KnowledgeDoc) =>
    !managedElsewhereNote(doc) && (isOrgOrPlatformAdmin || (!!userId && doc.uploadedUserId === userId));
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; doc: KnowledgeDoc } | null>(null);
  const [docToDelete, setDocToDelete] = useState<KnowledgeDoc | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Update adds the replacement first (through the normal add flow) and only
  // then deletes this doc, so cancelling or a failed upload loses nothing.
  const [docToReplace, setDocToReplace] = useState<KnowledgeDoc | null>(null);

  const fetchDocuments = useCallback(
    async (withLoader = true) => {
      if (withLoader) setLoadingDocs(true);
      setDocsError(null);
      try {
        const response = await API.getOrganizationDocuments(lastseenGroupId);
        const docs = response.documents || [];
        setOrgDocs(docs);
        if (pendingDocs.length) {
          setPendingDocs((prev) => prev.filter((pending) => !docs.some((doc) => doc._id === pending.id)));
        }
        if (lastUploadedId.current) {
          const matched = docs.find(
            (doc) => doc._id === lastUploadedId.current && doc.documentProcessingStatus === 'indexed'
          );
          if (matched) lastUploadedId.current = null;
        }
        return docs;
      } catch (err) {
        console.error('Failed to load documents', err);
        setDocsError('Unable to load documents. Please try again.');
        return [];
      } finally {
        if (withLoader) setLoadingDocs(false);
      }
    },
    [pendingDocs.length, lastseenGroupId]
  );

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    if (!lastseenGroupId || pendingDocs.length === 0) return undefined;
    const interval = setInterval(() => fetchDocuments(false), 5000);
    return () => clearInterval(interval);
  }, [fetchDocuments, pendingDocs.length, lastseenGroupId]);

  // Update's second step, once the replacement has uploaded: carry the old
  // doc's retrieval instruction over, then delete the old doc.
  const completeReplacement = useCallback(
    async (newDocId: string) => {
      const old = docToReplace;
      if (!old) return;
      setDocToReplace(null);
      try {
        if (old.instruction) await API.updateDocumentInstruction(newDocId, old.instruction);
        await API.deleteOrganizationDocument(old._id);
        toast.success(`"${old.title}" replaced.`);
      } catch (err) {
        console.error('Replace document error', err);
        toast.error(`The new version was added, but "${old.title}" couldn't be removed. Delete it from its row.`);
      }
    },
    [docToReplace]
  );

  const startReplace = (doc: KnowledgeDoc) => {
    setRowMenu(null);
    setDocToReplace(doc);
    toast.info(`Add the new version of "${doc.title}". The old one is removed once the new one uploads.`);
    if (doc.type === 'file') setOpenFileModal(true);
    else if (doc.type === 'text') setOpenTextModal(true);
    else setOpenUrlModal(true);
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;
    setDeleting(true);
    try {
      await API.deleteOrganizationDocument(docToDelete._id);
      toast.success(`"${docToDelete.title}" deleted. Elliott will no longer use it.`);
      setDocToDelete(null);
      await fetchDocuments(false);
    } catch (err: any) {
      console.error('Delete document error', err);
      toast.error(err?.response?.data?.message || 'The document could not be deleted. Try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleFileUpload = useCallback(
    async (file: File) => {
      if (!lastseenGroupId) {
        toast.error('No workspace selected. Please build or join a program first.');
        // Throw, so the dialog keeps what was typed instead of closing as if it had saved.
        throw new Error('No workspace selected');
      }
      try {
        const response = await API.processDocument(file, lastseenGroupId);
        const docId = response?.data?.id;
        if (!docId) throw new Error('Missing document id');
        lastUploadedId.current = docId;
        setPendingDocs((prev) => [...prev, { id: docId, name: file.name, type: 'file' }]);
        toast.success("File uploaded. We're indexing it now which will take a few minutes.");
        await completeReplacement(docId);
        await fetchDocuments(false);
      } catch (err) {
        console.error('File upload error', err);
        toast.error('The file could not be uploaded. Try again.');
        throw err;
      }
    },
    [completeReplacement, fetchDocuments, lastseenGroupId]
  );

  const handleAddText = useCallback(
    async (title: string, body: string) => {
      if (!lastseenGroupId) {
        toast.error('No workspace selected. Please build or join a program first.');
        // Throw, so the dialog keeps what was typed instead of closing as if it had saved.
        throw new Error('No workspace selected');
      }
      try {
        const response = await API.processTextDocument({ title, content: body, groupId: lastseenGroupId });
        const docId = response?.data?.id;
        if (!docId) throw new Error('Missing text document id');
        lastUploadedId.current = docId;
        setPendingDocs((prev) => [...prev, { id: docId, name: title, type: 'text' }]);
        toast.success("Text added. We're indexing it now which will take a few minutes.");
        await completeReplacement(docId);
        await fetchDocuments(false);
      } catch (err) {
        console.error('Add text error', err);
        toast.error('The text could not be saved. Try again.');
        throw err;
      }
    },
    [completeReplacement, fetchDocuments, lastseenGroupId]
  );

  const handleAddUrl = useCallback(
    async (title: string, url: string) => {
      if (!lastseenGroupId) {
        toast.error('No workspace selected. Please build or join a program first.');
        // Throw, so the dialog keeps what was typed instead of closing as if it had saved.
        throw new Error('No workspace selected');
      }
      try {
        const response = await API.processWebsiteDocument({ title, websiteUrl: url, groupId: lastseenGroupId });
        const docId = response?.data?.id;
        if (!docId) throw new Error('Missing website document id');
        lastUploadedId.current = docId;
        setPendingDocs((prev) => [...prev, { id: docId, name: title, type: 'webpage' }]);
        toast.success("URL added. We're indexing it now which will take a few minutes.");
        await completeReplacement(docId);
        await fetchDocuments(false);
      } catch (err) {
        console.error('Add URL error', err);
        toast.error('The URL could not be saved. Try again.');
        throw err;
      }
    },
    [completeReplacement, fetchDocuments, lastseenGroupId]
  );

  const renderDocRow = (doc: KnowledgeDoc, ready: boolean, isPending = false) => {
    const MIME_LABELS: Record<string, string> = {
      'pdf': 'PDF',
      'vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      'vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
      'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
      'msword': 'DOC',
      'vnd.ms-powerpoint': 'PPT',
      'vnd.ms-excel': 'XLS',
      'plain': 'TXT',
      'csv': 'CSV',
    };
    const mimeSubtype = doc.fileType?.split('/')?.pop() ?? '';
    const badgeLabel =
      doc.type === 'webpage'
        ? 'URL'
        : MIME_LABELS[mimeSubtype] || mimeSubtype.toUpperCase() || (doc.type || '').toUpperCase();
    // A doc still uploading has nothing to manage yet; every other row opens
    // its Update / Delete menu (or says where it's managed instead).
    const openRowMenu = (anchor: HTMLElement) => !isPending && setRowMenu({ anchor, doc });
    return (
      <Box
        key={doc._id}
        display="flex"
        flexDirection="column"
        gap={1.5}
        role={isPending ? undefined : 'button'}
        tabIndex={isPending ? undefined : 0}
        onClick={(e) => openRowMenu(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openRowMenu(e.currentTarget);
          }
        }}
        sx={{
          opacity: ready ? 1 : 0.6,
          mx: -1,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          cursor: isPending ? 'default' : 'pointer',
          '&:hover': isPending ? undefined : { bgcolor: 'rgba(255, 255, 255, 0.04)' }
        }}
      >
        <Box display="inline-flex" alignItems="center" justifyContent="space-between" width="100%">
          <Box display="flex" alignItems="center" gap={1.25} sx={{ [MOBILE_SX]: { minWidth: 0 } }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                p: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                [MOBILE_SX]: { flexShrink: 0 }
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '4px',
                  border: '2px solid var(--Stroke-icon-dim, #868686)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {[3, 8, 13].map((top) => (
                  <Box
                    key={top}
                    sx={{
                      position: 'absolute',
                      top,
                      left: 3,
                      right: 3,
                      height: 2,
                      background: 'var(--Stroke-icon-dim, #868686)'
                    }}
                  />
                ))}
              </Box>
            </Box>
            <Typography
              sx={{
                color: '#FDFDFD',
                fontSize: 16,
                fontWeight: 500,
                lineHeight: '14px',
                // A phone: a long title wraps, so it needs a real line height.
                [MOBILE_SX]: { fontSize: 15, lineHeight: 1.35, minWidth: 0, overflowWrap: 'anywhere' }
              }}
            >
              {doc.title}
            </Typography>
            <Box
              sx={{
                px: 1,
                py: '5px',
                background: 'var(--gray-700, #444444)',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                [MOBILE_SX]: { flexShrink: 0 }
              }}
            >
              <Typography
                sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', lineHeight: '14px' }}
              >
                {badgeLabel}
              </Typography>
            </Box>
            {doc.source === 'sharepoint' && (
              <Box
                sx={{
                  px: 1,
                  py: '5px',
                  background: 'rgba(0, 120, 212, 0.15)',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  [MOBILE_SX]: { flexShrink: 0 }
                }}
              >
                <Typography sx={{ color: '#0078D4', fontSize: 12, fontWeight: 500, lineHeight: '14px' }}>
                  SharePoint
                </Typography>
              </Box>
            )}
          </Box>
          {isPending && (
            <Chip
              label="Processing"
              size="small"
              sx={{ bgcolor: 'rgba(247, 148, 32, 0.12)', color: '#F79420', fontWeight: 600 }}
            />
          )}
          {!isPending && doc.documentProcessingStatus === 'failed' && (
            <Chip
              label="Failed"
              size="small"
              sx={{ bgcolor: 'rgba(244, 67, 54, 0.12)', color: '#F44336', fontWeight: 600 }}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#111111',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // A phone: as tall as its content; the app's scroll area already fills the screen.
        [MOBILE_SX]: { minHeight: 0, backgroundColor: 'transparent' }
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 900,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 3.75,
          pt: { xs: 4, md: 6 },
          px: { xs: 2, md: 3 },
          // A phone: the app shell already pads the page.
          [MOBILE_SX]: { gap: '16px', pt: '4px', px: 0 }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadFileIcon sx={{ color: '#FDFDFD' }} />
            <Typography sx={{ color: '#FDFDFD', fontSize: 20, fontWeight: 600 }}>Knowledge</Typography>
          </Box>
          <Typography sx={{ color: '#B6B6B6', fontSize: 13, lineHeight: 1.5 }}>
            New sources are indexed before they can be used, which usually takes a few minutes. Sources show a
            Processing tag until they&apos;re ready, and can&apos;t be selected when building a program until then.
          </Typography>
        </Box>

        {/* A phone: the three add cards stack, each the full width. */}
        <Box sx={{ display: 'flex', gap: 1.875, width: '100%', [MOBILE_SX]: { flexDirection: 'column', gap: '10px' } }}>
          <Box
            onClick={() => setOpenFileModal(true)}
            sx={{
              flex: 1,
              p: '12px 15px',
              borderRadius: '6px',
              outline: '1px solid #868686',
              outlineOffset: '-1px',
              display: 'flex',
              gap: 1.25,
              cursor: 'pointer'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
                <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700, lineHeight: '14px' }}>
                  Add Files
                </Typography>
                <UploadFileIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />
              </Box>
              <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, lineHeight: '20px' }}>
                Upload PDFs or images that should inform Elliott&apos;s answers.
              </Typography>
            </Box>
          </Box>

          <Box
            onClick={() => setOpenTextModal(true)}
            sx={{
              flex: 1,
              p: '12px 15px',
              borderRadius: '6px',
              outline: '1px solid #868686',
              outlineOffset: '-1px',
              display: 'flex',
              gap: 1.25,
              cursor: 'pointer'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
                <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700, lineHeight: '14px' }}>
                  Add Text
                </Typography>
                <ArticleIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />
              </Box>
              <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, lineHeight: '20px' }}>
                Paste or type content that Elliott should use.
              </Typography>
            </Box>
          </Box>

          <Box
            onClick={() => setOpenUrlModal(true)}
            sx={{
              flex: 1,
              p: '12px 15px',
              borderRadius: '6px',
              outline: '1px solid #868686',
              outlineOffset: '-1px',
              display: 'flex',
              gap: 1.25,
              cursor: 'pointer'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
                <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700, lineHeight: '14px' }}>
                  Add URL
                </Typography>
                <LinkIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />
              </Box>
              <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, lineHeight: '20px' }}>
                Share a webpage or online resource that should inform Elliott&apos;s answers.
              </Typography>
            </Box>
          </Box>
        </Box>

        <ConnectedSources orgId={orgId} onSyncComplete={() => fetchDocuments(false)} />

        {docsError && (
          <Typography color="error.main" sx={{ mb: 1 }}>
            {docsError}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          {(['file', 'text', 'webpage', 'sharepoint'] as const).map((docType) => (
            <Accordion
              key={docType}
              sx={{
                background: '#1A1A1A',
                border: '1px solid #2A2A2A',
                borderRadius: 1,
                overflow: 'hidden',
                // A phone: an open source list keeps the list's own spacing, without MUI's extra margin.
                [MOBILE_SX]: { '&.Mui-expanded': { my: 0 } }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#B6B6B6' }} />}>
                <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 600, textTransform: 'capitalize' }}>
                  {docType === 'file'
                    ? 'Files'
                    : docType === 'text'
                      ? 'Text'
                      : docType === 'webpage'
                        ? 'URLs'
                        : 'SharePoint'}{' '}
                  (
                  {docType === 'sharepoint'
                    ? orgDocs.filter((d) => (d as OrgDocument & { source?: string }).source === 'sharepoint').length
                    : orgDocs.filter((d) => d.type === docType).length +
                      pendingDocs.filter((d) => d.type === docType).length}
                  )
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {loadingDocs ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {[0, 1, 2].map((i) => (
                      <Bone key={i} height={56} />
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, ...fadeInSx }}>
                    {docType !== 'sharepoint' &&
                      pendingDocs
                        .filter((d) => d.type === docType)
                        .map((doc) =>
                          renderDocRow(
                            {
                              _id: doc.id,
                              title: doc.name,
                              type: doc.type as 'file' | 'webpage' | 'text',
                              fileType: '',
                              fileName: doc.name,
                              createdAt: Date.now(),
                              documentProcessingStatus: 'processing',
                              uploadedUserId: ''
                            },
                            false,
                            true
                          )
                        )}
                    {docType === 'sharepoint' &&
                      orgDocs.filter((d) => (d as OrgDocument & { source?: string }).source === 'sharepoint')
                        .length === 0 && (
                        <Typography sx={{ color: '#868686' }}>
                          No SharePoint documents synced yet.
                        </Typography>
                      )}
                    {docType !== 'sharepoint' &&
                      orgDocs.filter((d) => d.type === docType).length === 0 &&
                      pendingDocs.filter((d) => d.type === docType).length === 0 && (
                        <Typography sx={{ color: '#868686' }}>
                          No {docType === 'file' ? 'files uploaded' : docType === 'webpage' ? 'URLs added' : 'text added'}{' '}
                          yet.
                        </Typography>
                      )}
                    {docType === 'sharepoint'
                      ? orgDocs
                          .filter((d) => (d as OrgDocument & { source?: string }).source === 'sharepoint')
                          .map((doc) =>
                            renderDocRow(
                              doc as OrgDocument & { source?: string },
                              doc.documentProcessingStatus === 'indexed',
                              doc.documentProcessingStatus === 'processing'
                            )
                          )
                      : orgDocs
                          .filter((d) => d.type === docType)
                          .map((doc) =>
                            renderDocRow(
                              doc,
                              doc.documentProcessingStatus === 'indexed',
                              doc.documentProcessingStatus === 'processing'
                            )
                          )}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* The add modals close only after a successful add, so closing one
            either finished a replacement or cancelled it — never both. */}
        <AddFileModal
          open={openFileModal}
          onClose={() => {
            setOpenFileModal(false);
            setDocToReplace(null);
          }}
          onUpload={handleFileUpload}
        />
        <AddTextModal
          open={openTextModal}
          onClose={() => {
            setOpenTextModal(false);
            setDocToReplace(null);
          }}
          onCreate={handleAddText}
        />
        <AddURLModal
          open={openUrlModal}
          onClose={() => {
            setOpenUrlModal(false);
            setDocToReplace(null);
          }}
          onCreate={handleAddUrl}
        />

        <Menu
          anchorEl={rowMenu?.anchor}
          open={!!rowMenu}
          onClose={() => setRowMenu(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{ sx: { bgcolor: '#1C1C1C', border: '1px solid #2A2A2A', color: '#FDFDFD', minWidth: 200 } }}
        >
          {rowMenu && managedElsewhereNote(rowMenu.doc) ? (
            <MenuItem disabled sx={{ whiteSpace: 'normal', maxWidth: 320, fontSize: 14 }}>
              {managedElsewhereNote(rowMenu.doc)}
            </MenuItem>
          ) : rowMenu && !canChange(rowMenu.doc) ? (
            <MenuItem disabled sx={{ whiteSpace: 'normal', maxWidth: 320, fontSize: 14 }}>
              Only an admin or the person who added this can change it.
            </MenuItem>
          ) : (
            [
              <MenuItem key="update" onClick={() => rowMenu && startReplace(rowMenu.doc)} sx={{ fontSize: 14 }}>
                Update (add a new version)
              </MenuItem>,
              <MenuItem
                key="delete"
                onClick={() => {
                  setDocToDelete(rowMenu?.doc || null);
                  setRowMenu(null);
                }}
                sx={{ fontSize: 14, color: '#F44336' }}
              >
                Delete
              </MenuItem>
            ]
          )}
        </Menu>

        <ConfirmDialog
          open={!!docToDelete}
          title="Delete this source?"
          message={`"${
            docToDelete?.title || ''
          }" will be removed from Elliott's knowledge straight away. This can't be undone.`}
          confirmLabel="Delete"
          busy={deleting}
          busyLabel="Deleting..."
          onConfirm={confirmDelete}
          onClose={() => setDocToDelete(null)}
        />
      </Box>
    </Box>
  );
};

export default Knowledge;
